export type RuleSummaryPresentationStatus = "complete" | "partial" | "unsupported";

export type RuleSummaryPresentationSource =
    | "native_appglue_expression"
    | "native_appglue_config"
    | "adapter_routing"
    | "adapter_projection"
    | "demo_definition";

export type RuleSummaryValue = string | number | boolean | null | undefined | string[] | number[];

export type RuleSummaryOperand =
    | {
          kind: "field";
          field: string;
      }
    | {
          kind: "literal";
          value: RuleSummaryValue;
      }
    | {
          kind: "function";
          name: string;
          args: RuleSummaryOperand[];
      }
    | {
          kind: "native_expression";
          label: string;
      };

export type RuleSummaryExpression =
    | {
          kind: "comparison";
          left: RuleSummaryOperand;
          operator: "==" | "!=" | ">" | ">=" | "<" | "<=";
          right: RuleSummaryOperand;
      }
    | {
          kind: "in_list";
          left: RuleSummaryOperand;
          values: RuleSummaryValue[];
      }
    | {
          kind: "exists";
          field: string;
      }
    | {
          kind: "contains";
          left: RuleSummaryOperand;
          right: RuleSummaryOperand;
      }
    | {
          kind: "starts_with";
          left: RuleSummaryOperand;
          right: RuleSummaryOperand;
      }
    | {
          kind: "and";
          expressions: RuleSummaryExpression[];
      }
    | {
          kind: "not";
          expression: RuleSummaryExpression;
      }
    | {
          kind: "unsupported";
          label: string;
      };

export type RuleSummaryEditPortReference = {
    conditionId?: string;
    requirementId?: string;
    canEditField?: boolean;
    canEditValues?: boolean;
};

export type RuleSummaryStatement =
    | {
          kind: "text";
          text: string;
      }
    | {
          kind: "condition";
          keyword: "IF" | "ELSE IF";
          expression: RuleSummaryExpression;
          result?: RuleSummaryValue;
          editPort?: RuleSummaryEditPortReference;
      }
    | {
          kind: "else";
          result: RuleSummaryValue;
      }
    | {
          kind: "requirement";
          fields: string[];
          editPort?: RuleSummaryEditPortReference;
      }
    | {
          kind: "action";
          label: string;
      }
    | {
          kind: "result";
          value: RuleSummaryValue;
      };

export type RuleSummaryAst = {
    kind: "rule_summary";
    statements: RuleSummaryStatement[];
};

export type RuleSummaryPresentation = {
    status: RuleSummaryPresentationStatus;
    ast: RuleSummaryAst;
    lines: string[];
    editable: boolean;
    sources: RuleSummaryPresentationSource[];
    warnings: string[];
};

export type RuleSimplifiedLogicFieldOption = {
    value: string;
    label: string;
};

export type RuleSimplifiedLogicConditionEditorModel = {
    id: string;
    keyword: "IF";
    field: string;
    fieldOptions: RuleSimplifiedLogicFieldOption[];
    operatorLabel: "==" | "in" | ">" | ">=" | "<" | "<=";
    values: string[];
    valueOptions: RuleSimplifiedLogicFieldOption[];
    valueInputLabel: string;
};

export type RuleSimplifiedLogicRequirementEditorModel = {
    id: string;
    keyword: "REQUIRE";
    values: string[];
    valueOptions: RuleSimplifiedLogicFieldOption[];
    valueInputLabel: string;
};

export type RuleSummaryEditModel = {
    condition: RuleSimplifiedLogicConditionEditorModel;
    requirement?: RuleSimplifiedLogicRequirementEditorModel;
};

export type RuleSimplifiedLogicEditorModel = RuleSummaryEditModel;

export type RuleSimplifiedLogicEdit = {
    conditionId: string;
    field?: string;
    values?: string[];
    requirementId?: string;
    requirementValues?: string[];
};

const valueToText = (value: RuleSummaryValue): string => {
    if (Array.isArray(value)) return value.map(String).join(", ");
    if (typeof value === "string" && (value === "" || value.trim() !== value)) {
        return JSON.stringify(value);
    }
    return value === undefined ? "undefined" : String(value);
};

export const renderRuleSummaryOperand = (operand: RuleSummaryOperand): string => {
    if (operand.kind === "field") return operand.field;
    if (operand.kind === "function") {
        return `${operand.name}(${operand.args.map(renderRuleSummaryOperand).join(", ")})`;
    }
    if (operand.kind === "native_expression") return operand.label;
    return valueToText(operand.value);
};

export const renderRuleSummaryExpression = (expression: RuleSummaryExpression): string => {
    if (expression.kind === "comparison") {
        return `${renderRuleSummaryOperand(expression.left)} ${expression.operator} ${renderRuleSummaryOperand(
            expression.right,
        )}`;
    }
    if (expression.kind === "in_list") {
        return `${renderRuleSummaryOperand(expression.left)} in ${expression.values.map(valueToText).join(", ")}`;
    }
    if (expression.kind === "exists") return `${expression.field} exists`;
    if (expression.kind === "contains") {
        return `${renderRuleSummaryOperand(expression.left)} contains ${renderRuleSummaryOperand(expression.right)}`;
    }
    if (expression.kind === "starts_with") {
        return `${renderRuleSummaryOperand(expression.left)} starts_with ${renderRuleSummaryOperand(expression.right)}`;
    }
    if (expression.kind === "and") return expression.expressions.map(renderRuleSummaryExpression).join(" AND ");
    if (expression.kind === "not") return `NOT (${renderRuleSummaryExpression(expression.expression)})`;
    return expression.label;
};

export const renderRuleSummaryAst = (ast: RuleSummaryAst): string[] =>
    ast.statements.map((statement) => {
        if (statement.kind === "text") return statement.text;
        if (statement.kind === "condition") {
            const condition = `${statement.keyword} ${renderRuleSummaryExpression(statement.expression)}`;
            return statement.result === undefined ? condition : `${condition} THEN ${valueToText(statement.result)}`;
        }
        if (statement.kind === "else") return `ELSE ${valueToText(statement.result)}`;
        if (statement.kind === "requirement") {
            return statement.fields.length > 0
                ? `REQUIRE ${statement.fields.join(", ")}`
                : "REQUIRE no additional fields";
        }
        if (statement.kind === "action") return `ACTION ${statement.label}`;
        return `RESULT ${valueToText(statement.value)}`;
    });

export const createRuleSummaryAstFromLines = (lines: string[]): RuleSummaryAst => ({
    kind: "rule_summary",
    statements: lines.map((line) => ({ kind: "text", text: line })),
});

export const createRuleSummaryPresentation = ({
    status,
    lines,
    ast,
    editable = false,
    sources,
    warnings = [],
}: {
    status: RuleSummaryPresentationStatus;
    lines?: string[];
    ast?: RuleSummaryAst;
    editable?: boolean;
    sources: RuleSummaryPresentationSource[];
    warnings?: string[];
}): RuleSummaryPresentation => {
    const resolvedAst = ast ?? createRuleSummaryAstFromLines(lines ?? []);

    return {
        status,
        ast: resolvedAst,
        lines: lines ?? renderRuleSummaryAst(resolvedAst),
        editable: status === "complete" ? editable : false,
        sources,
        warnings,
    };
};
